import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";

/**
 * ProtectedRoute
 *
 * Two-layer guard:
 *   Layer 1 — Authentication: is there a valid Supabase session?
 *     No  → redirect to /auth (preserves `state.from` for post-login redirect)
 *
 *   Layer 2 — Onboarding: does the authenticated user have a completed profile?
 *     "Completed" = a row in the `users` table with target_language set.
 *     No  → redirect to /onboarding
 *     Yes → render the child <Outlet />
 *
 * This ensures no user can reach /dashboard or /dashboard/session without:
 *   a) being signed in, AND
 *   b) having chosen their language and learning goal.
 *
 * The /onboarding route itself is behind this guard intentionally:
 * only authenticated users can complete onboarding.
 *
 * Exception: /onboarding itself must not re-redirect to /onboarding
 * → handled by the `skipOnboardingCheck` check on pathname.
 */

type LoadState = "loading" | "unauthenticated" | "needs_onboarding" | "ready";

const ProtectedRoute: React.FC = () => {
  const location = useLocation();
  const [state, setState] = useState<LoadState>("loading");

  useEffect(() => {
    let cancelled = false;

    async function check() {
      // Step 1: get current auth session
      const { data: sessionData } = await supabase.auth.getSession();
      const session = sessionData.session;

      if (!session) {
        if (!cancelled) setState("unauthenticated");
        return;
      }

      // Step 2: check if onboarding is complete.
      // We consider onboarding done when the users row exists with target_language set.
      // If we're already on /onboarding, skip this check to avoid redirect loops.
      const isOnboardingPage = location.pathname === "/onboarding";
      if (isOnboardingPage) {
        if (!cancelled) setState("ready");
        return;
      }

      const { data: profile } = await supabase
        .from("users")
        .select("target_language")
        .eq("id", session.user.id)
        .maybeSingle();

      if (!cancelled) {
        if (!profile?.target_language) {
          setState("needs_onboarding");
        } else {
          setState("ready");
        }
      }
    }

    check();

    // Keep in sync with auth state changes (logout from another tab, token refresh)
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (!sess) setState("unauthenticated");
      else check();
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ── Loading: render a minimal pulse to prevent layout flash ──────────────
  if (state === "loading") {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center">
        <div className="w-2.5 h-2.5 rounded-full bg-aura-cyan animate-pulse shadow-[0_0_14px_rgb(var(--accent-cyan-rgb)/0.8)]" />
      </div>
    );
  }

  // ── Not authenticated ────────────────────────────────────────────────────
  if (state === "unauthenticated") {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // ── Authenticated but no profile → force onboarding ─────────────────────
  if (state === "needs_onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  // ── Authenticated + profile complete ─────────────────────────────────────
  return <Outlet />;
};

export default ProtectedRoute;
